import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import tippy from 'tippy.js';
import { ReactRenderer } from '@tiptap/react';
import { SlashCommandsList } from '../components/SlashCommandsList';
import { BibleSelector } from '../components/BibleSelector';
import { CollectionSelector } from '../components/CollectionSelector';
import 'tippy.js/dist/tippy.css';

interface CommandProps {
  editor: any;
  range: any;
}

interface Command {
  title: string;
  description: string;
  command: (props: CommandProps) => void;
}

export const SlashCommands = Extension.create({
  name: 'slashCommands',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        command: ({ editor, range, props }: { editor: any; range: any; props: any }) => {
          props.command({ editor, range });
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        // Limit the number of items to improve performance
        maxItems: 7,
        // Debounce the suggestions to prevent excessive rendering
        debounce: 200,
        items: ({ query }: { query: string }): Command[] => {
          const { $from } = this.editor.state.selection;
          let isInSermonSection = false;
          let sectionDepth = 0;
          
          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (node.type.name === 'sermonSection') {
              isInSermonSection = true;
              sectionDepth = depth;
              break;
            }
          }

          if (isInSermonSection) {
            return [
              {
                title: 'Delete Section',
                description: 'Delete this section',
                command: ({ editor, range }) => {
                  if (window.confirm('Are you sure you want to delete this section?')) {
                    editor
                      .chain()
                      .focus()
                      .deleteRange(range)
                      .command(({ tr, dispatch }) => {
                        if (dispatch) {
                          const { $from } = tr.selection;
                          let sectionPos = 0;
                          let sectionNode = null;
                          
                          // Find the section node and its position
                          for (let d = $from.depth; d > 0; d--) {
                            const node = $from.node(d);
                            if (node.type.name === 'sermonSection') {
                              sectionNode = node;
                              sectionPos = $from.before(d);
                              break;
                            }
                          }

                          if (!sectionNode) return false;

                          // Delete the entire section and replace with a paragraph
                          const newParagraph = editor.schema.nodes.paragraph.create();
                          tr.replaceWith(sectionPos, sectionPos + sectionNode.nodeSize, newParagraph);
                          
                          // Move cursor to the new paragraph
                          const resolvedPos = tr.doc.resolve(sectionPos + 1);
                          tr.setSelection(editor.state.selection.constructor.near(resolvedPos));
                        }
                        return true;
                      })
                      .run();
                  }
                },
              }
            ].filter(item =>
              item.title.toLowerCase().includes(query.toLowerCase())
            );
          }

          // Limit the number of commands based on query to improve performance
          const allCommands = [
            {
              title: 'Scripture',
              description: 'Add a Scripture passage',
              command: ({ editor, range }) => {
                let bibleSelector: any;
                let popup: any;

                const onSelect = (passage: any) => {
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .setSermonSection('scripture')
                    .insertContent([
                      {
                        type: 'paragraph',
                        content: [
                          {
                            type: 'text',
                            text: `${passage.reference} (${passage.version})\n\n${passage.text}`,
                          },
                        ],
                      },
                    ])
                    .run();
                  
                  popup[0].destroy();
                  bibleSelector.destroy();
                };

                const onClose = () => {
                  popup[0].destroy();
                  bibleSelector.destroy();
                };

                bibleSelector = new ReactRenderer(BibleSelector, {
                  props: {
                    onSelect,
                    onClose,
                  },
                  editor,
                });

                const coords = editor.view.coordsAtPos(range.from);
                const domRect = new DOMRect(
                  coords.left,
                  coords.top,
                  0,
                  0
                );

                popup = tippy('body', {
                  getReferenceClientRect: () => domRect,
                  appendTo: () => document.body,
                  content: bibleSelector.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'top-start',
                  theme: 'light',
                  arrow: true,
                  zIndex: 100000,
                });
              },
            },
            {
              title: 'Collection',
              description: 'Insert from your collections',
              command: ({ editor, range }) => {
                let collectionSelector: any;
                let popup: any;

                const onSelect = (collection: any) => {
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .insertContent(collection.content)
                    .run();
                  
                  popup[0].destroy();
                  collectionSelector.destroy();
                };

                const onClose = () => {
                  popup[0].destroy();
                  collectionSelector.destroy();
                };

                collectionSelector = new ReactRenderer(CollectionSelector, {
                  props: {
                    onSelect,
                    onClose,
                  },
                  editor,
                });

                const coords = editor.view.coordsAtPos(range.from);
                const domRect = new DOMRect(
                  coords.left,
                  coords.top,
                  0,
                  0
                );

                popup = tippy('body', {
                  getReferenceClientRect: () => domRect,
                  appendTo: () => document.body,
                  content: collectionSelector.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'top-start',
                  theme: 'light',
                  arrow: true,
                  zIndex: 100000,
                });
              },
            },
            {
              title: 'Illustration',
              description: 'Add an illustration or story',
              command: ({ editor, range }) => {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .setSermonSection('illustration')
                  .run();
              },
            },
            {
              title: 'Quote',
              description: 'Add a quote with attribution',
              command: ({ editor, range }) => {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .setSermonSection('quote')
                  .run();
              },
            },
            {
              title: 'Application',
              description: 'Add an application point',
              command: ({ editor, range }) => {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .setSermonSection('application')
                  .run();
              },
            },
            {
              title: 'Main Point',
              description: 'Add a main point',
              command: ({ editor, range }) => {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .setSermonSection('main-point')
                  .run();
              },
            },
            {
              title: 'Custom',
              description: 'Add a custom section',
              command: ({ editor, range }) => {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .setSermonSection('custom')
                  .run();
              },
            }
          ];

          // Filter commands based on query
          return allCommands.filter(item =>
            item.title.toLowerCase().includes(query.toLowerCase())
          );
        },
        render: () => {
          let component: any;
          let popup: any;

          return {
            onStart: (props: any) => {
              component = new ReactRenderer(SlashCommandsList, {
                props,
                editor: props.editor,
              });

              const coords = props.editor.view.coordsAtPos(props.range.from);
              const domRect = new DOMRect(
                coords.left,
                coords.top,
                0,
                0
              );

              popup = tippy('body', {
                getReferenceClientRect: () => domRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'top-start',
                theme: 'light',
                arrow: true,
                zIndex: 99999,
                // Add performance optimizations
                popperOptions: {
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        padding: 10,
                      },
                    },
                    {
                      name: 'flip',
                      options: {
                        padding: 10,
                      },
                    },
                  ],
                },
              });
            },
            onUpdate(props: any) {
              component?.updateProps(props);

              const coords = props.editor.view.coordsAtPos(props.range.from);
              const domRect = new DOMRect(
                coords.left,
                coords.top,
                0,
                0
              );

              popup?.[0].setProps({
                getReferenceClientRect: () => domRect,
              });
            },
            onKeyDown(props: any) {
              if (props.event.key === 'Escape') {
                popup?.[0].hide();
                return true;
              }

              return component?.ref?.onKeyDown(props);
            },
            onExit() {
              popup?.[0].destroy();
              component?.destroy();
            },
          };
        },
      }),
    ];
  },
});